import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon, SkeletonText } from '@atoms'
import { UserMenu } from '@/components/organisms'
import { Dropdown, LinkButton } from '..'
import useAuthStore from '../../../stores/useAuthStore/useAuthStore'
import { truncateEmail } from '../../../utils/truncateEmail'
import { useDropdownClickOutside } from '../Dropdown/useDropdownClickOutside'

type HeaderProps = React.PropsWithChildren

export const Header: React.FC<HeaderProps> = () => {
    const { t } = useTranslation('translations')
    const user = useAuthStore((state) => state.user)
    const [isOpen, setIsOpen] = useState(false)

    const fullName = useMemo(() => {
        if (user?.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`
        }
        return <SkeletonText />
    }, [user])

    const email = useMemo(() => {
        if (user?.email) {
            return truncateEmail(user.email)
        }

        return <SkeletonText size="small" />
    }, [user])

    const { buttonRef, dropdownRef } = useDropdownClickOutside(() => {
        if (isOpen) {
            setIsOpen(false)
        }
    })

    return (
        <header className="flex justify-end px-6 py-4">
            <Dropdown
                name="profile-dropdown"
                isOpen={isOpen}
                buttonProps={{
                    variant: 'secondary',
                    iconAfter: 'keyboard_arrow_down',
                    label: (
                        <div className="flex items-center gap-1">
                            <Icon name="account_circle" size="small" />
                            <div>{t('common.account')}</div>
                        </div>
                    ),
                    name: 'profile-dropdown-button',
                    buttonRef,
                }}
                buttonComponent={LinkButton}
                horizontalAlign="right"
                dropdownClassname="py-2 !z-[2001] min-w-userDropdownWidth"
                dropdownRef={dropdownRef}
                onClick={() => {
                    setIsOpen((prev) => !prev)
                }}
            >
                <div className="space-y-2">
                    <div className="space-y-[1px] px-4 py-2">
                        <div className="text-label-small text-regular">
                            {fullName}
                        </div>
                        <div className="text-body-small whitespace-nowrap text-regular">
                            {email}
                        </div>
                    </div>
                    <div className="border-t border-neutral-200" />
                    <UserMenu />
                </div>
            </Dropdown>
        </header>
    )
}
